/**
 * Holistic Onboarding Wizard Types
 * Types for the data discovery and onboarding pipeline
 */

// ============================================================================
// Session Status
// ============================================================================

export type OnboardingStatus =
  | "draft" // Initial state, collecting data
  | "analyzing" // LLM processing
  | "ready" // Report ready for review
  | "importing" // User initiated import
  | "completed" // Import finished
  | "failed"; // Error occurred

export type SectionName =
  | "companyProfile"
  | "financialAccounts"
  | "vendorsAndClients"
  | "productsAndInvoices";

// ============================================================================
// Company Profile Section
// ============================================================================

export interface OnboardingAddress {
  street?: string;
  city?: string;
  zip?: string;
  canton?: string;
  country?: string;
}

export interface OnboardingContact {
  phone?: string;
  email?: string;
  website?: string;
}

export interface OnboardingCompanyProfile {
  name?: string;
  uid?: string; // Swiss UID (CHE-xxx.xxx.xxx)
  legalForm?: string; // AG, GmbH, Einzelunternehmen, etc.
  address?: OnboardingAddress;
  contact?: OnboardingContact;
  industry?: string;
  foundedDate?: number; // Unix timestamp

  // Tax information
  canton?: string; // Tax canton (ZH, GE, BE, etc.)
  vatNumber?: string; // MWST number
  vatRegistered?: boolean;
  fiscalYearEnd?: string; // e.g., "12-31" for calendar year

  confidence: number; // 0-100
  sources: string[]; // Document names that provided this data
}

// ============================================================================
// Financial Accounts Section
// ============================================================================

export interface OnboardingBankAccount {
  id: string; // Unique ID for checkbox tracking
  iban?: string;
  bank?: string;
  bankCode?: string;
  accountName?: string;
  accountType:
    | "bank_account"
    | "credit_card"
    | "digital_wallet"
    | "payment_processor";
  currency?: string;

  // Statistics from transactions
  transactionCount?: number;
  dateRange?: {
    from: string;
    to: string;
  };
  totalCredits?: number; // In cents
  totalDebits?: number; // In cents

  confidence: number;
  source: string; // Document that provided this data
}

export interface OnboardingFinancialAccounts {
  accounts: OnboardingBankAccount[];
  totalAccounts: number;
  confidence: number;
}

// ============================================================================
// Vendors & Clients Section
// ============================================================================

export interface OnboardingVendor {
  id: string; // Unique ID for checkbox tracking
  name: string;
  normalizedName?: string;
  category?: string;
  subcategory?: string;

  // Swiss-specific
  uid?: string; // Swiss UID if known
  mwstRegistered?: boolean;

  // Address
  street?: string;
  zip?: string;
  city?: string;
  canton?: string;
  country?: string;

  // Transaction statistics
  transactionCount: number;
  totalAmount: number; // In cents (negative for expenses)
  averageAmount?: number;
  firstSeen?: string;
  lastSeen?: string;

  // Suggestions
  suggestedAccountCode?: string;
  suggestedMwstRate?: "0" | "2.5" | "3.7" | "7.7";

  confidence: number;
}

export interface OnboardingClient {
  id: string; // Unique ID for checkbox tracking
  name: string;
  normalizedName?: string;
  customerType?: "business" | "individual" | "unknown";

  // Address
  country?: string;

  // Transaction statistics
  transactionCount: number;
  totalRevenue: number; // In cents
  averageTransaction?: number;
  firstSeen?: string;
  lastSeen?: string;

  confidence: number;
}

export interface OnboardingVendorsClients {
  vendors: OnboardingVendor[];
  clients: OnboardingClient[];
  totalVendors: number;
  totalClients: number;
  vendorConfidence: number;
  clientConfidence: number;
}

// ============================================================================
// Products & Invoices Section
// ============================================================================

export interface OnboardingProduct {
  id: string;
  name: string;
  type: "goods" | "service" | "subscription" | "license" | "utility" | "other";

  // Vendor association
  vendorId?: string;
  vendorName?: string;

  // Pricing
  unitPrice?: number; // In cents
  currency?: string;

  // Recurrence
  isRecurring: boolean;
  frequency?:
    | "one_time"
    | "daily"
    | "weekly"
    | "monthly"
    | "quarterly"
    | "yearly";

  // Statistics
  purchaseCount: number;
  totalSpent: number; // In cents
  firstPurchase?: string;
  lastPurchase?: string;

  confidence: number;
}

export interface OnboardingInvoice {
  id: string;
  invoiceNumber?: string;
  date?: string;
  dueDate?: string;

  // Parties
  vendorId?: string;
  vendorName?: string;
  clientId?: string;
  clientName?: string;

  // Direction
  direction: "incoming" | "outgoing";

  // Amounts
  subtotal?: number; // In cents
  vatAmount?: number;
  total?: number;

  // Status
  isPaid?: boolean;
  paymentDate?: string;

  confidence: number;
  source: string;
}

export interface OnboardingProductsInvoices {
  products: OnboardingProduct[];
  invoices: OnboardingInvoice[];
  totalProducts: number;
  totalInvoices: number;
  recurringChargesCount: number;
  confidence: number;
}

// ============================================================================
// Assumptions
// ============================================================================

export interface OnboardingAssumption {
  id: string;
  section: SectionName;
  field: string; // e.g., "canton", "vatRegistered"
  fieldLabel: string; // Human-readable label
  assumedValue: string;
  reason: string; // Why this assumption was made
  confidence: number; // 0-100
  overrideValue?: string; // User override if any
  overridden: boolean;
}

// ============================================================================
// Section Toggles & Exclusions
// ============================================================================

export interface EnabledSections {
  companyProfile: boolean;
  financialAccounts: boolean;
  vendorsAndClients: boolean;
  productsAndInvoices: boolean;
}

export interface ExcludedItems {
  vendorIds: string[];
  clientIds: string[];
  accountIds: string[];
  productIds: string[];
}

// ============================================================================
// Analysis Results
// ============================================================================

export interface AnalysisResults {
  aggregatedFinancials?: {
    cash: number;
    receivables: number;
    inventory: number;
    currentAssets: number;
    fixedAssets: number;
    totalAssets: number;
    currentLiabilities: number;
    shortTermDebt: number;
    longTermDebt: number;
    totalLiabilities: number;
    equity: number;
    revenue: number;
    materialCosts: number;
    grossProfit: number;
    personnelExpenses: number;
    operatingExpenses: number;
    depreciation: number;
    ebitda: number;
    ebit: number;
    interestExpense: number;
    taxExpense: number;
    netIncome: number;
  };
  transactionCount: number;
  analyzedAt: number;
  error?: string;
}

// ============================================================================
// Import Results
// ============================================================================

export interface ImportResults {
  companyCreated: boolean;
  companyId?: number;
  bankAccountsCreated: number;
  vendorsCreated: number;
  clientsCreated: number;
  productsCreated: number;
  errors: string[];
}

// ============================================================================
// Full Session
// ============================================================================

export interface OnboardingSession {
  id: number;
  userId: number;
  companyId?: number; // null until company created/linked

  name: string;
  status: OnboardingStatus;

  // Data sources
  documentIds: number[];
  intelligenceSessionIds: number[];
  discoverySessionId?: number;

  // Section data
  companyProfile?: OnboardingCompanyProfile;
  financialAccounts?: OnboardingFinancialAccounts;
  vendorsAndClients?: OnboardingVendorsClients;
  productsAndInvoices?: OnboardingProductsInvoices;

  // Toggles and exclusions
  enabledSections: EnabledSections;
  excludedItems: ExcludedItems;

  // Confidence
  overallConfidence?: number;
  sectionConfidences?: Record<SectionName, number>;

  // Assumptions
  assumptions: OnboardingAssumption[];

  // Analysis results (from aggregation engine)
  analysisResults?: AnalysisResults;

  // Import results
  importResults?: ImportResults;

  // Timestamps
  createdAt: number;
  updatedAt: number;
  completedAt?: number;

  // Error
  error?: string;
}

// ============================================================================
// API Input/Output Types
// ============================================================================

export interface CreateSessionInput {
  name: string;
  companyId?: number;
  documentIds?: number[];
  intelligenceSessionIds?: number[];
  discoverySessionId?: number;
}

export interface CreateSessionResult {
  sessionId: number;
  status: OnboardingStatus;
}

export interface AddDocumentsInput {
  sessionId: number;
  files: Array<{
    name: string;
    content: string; // Base64 encoded
    mimeType: string;
    size: number;
  }>;
}

export interface ToggleSectionInput {
  sessionId: number;
  section: SectionName;
  enabled: boolean;
}

export interface ToggleItemInput {
  sessionId: number;
  itemType: "vendor" | "client" | "account" | "product";
  itemId: string;
  excluded: boolean;
}

export interface UpdateAssumptionInput {
  sessionId: number;
  assumptionId: string;
  overrideValue: string;
}

export interface ImportPreview {
  companyWillBeCreated: boolean;
  bankAccountsToCreate: number;
  vendorsToCreate: number;
  clientsToCreate: number;
  productsToCreate: number;

  // Details
  companyName?: string;
  bankAccounts: Array<{ iban?: string; bank?: string }>;
  topVendors: Array<{ name: string; totalAmount: number }>;
  topClients: Array<{ name: string; totalRevenue: number }>;

  warnings: string[];
}

// ============================================================================
// Helper Functions
// ============================================================================

export function createEmptySession(
  userId: number,
  name: string
): Partial<OnboardingSession> {
  return {
    userId,
    name,
    status: "draft",
    documentIds: [],
    intelligenceSessionIds: [],
    enabledSections: {
      companyProfile: true,
      financialAccounts: true,
      vendorsAndClients: true,
      productsAndInvoices: true,
    },
    excludedItems: {
      vendorIds: [],
      clientIds: [],
      accountIds: [],
      productIds: [],
    },
    assumptions: [],
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
}

export function calculateOverallConfidence(session: OnboardingSession): number {
  const scores: number[] = [];

  if (session.companyProfile?.confidence) {
    scores.push(session.companyProfile.confidence);
  }

  if (session.financialAccounts?.confidence) {
    scores.push(session.financialAccounts.confidence);
  }

  if (session.vendorsAndClients) {
    const avgVendorClient =
      (session.vendorsAndClients.vendorConfidence +
        session.vendorsAndClients.clientConfidence) /
      2;
    scores.push(avgVendorClient);
  }

  if (session.productsAndInvoices?.confidence) {
    scores.push(session.productsAndInvoices.confidence);
  }

  if (scores.length === 0) return 0;

  return Math.round(scores.reduce((sum, s) => sum + s, 0) / scores.length);
}

/**
 * Format Swiss amount with CHF and apostrophe thousands separator
 */
export function formatChf(amountInCents: number): string {
  const amount = amountInCents / 100;
  const formatted = amount.toLocaleString("de-CH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `CHF ${formatted}`;
}
